import { useMemo } from "react";
import { format } from "date-fns";
import { AlertTriangle, Lock } from "lucide-react";
import { useAccountSubscriptions, seatState } from "@/hooks/useAccountSubscriptions";

/**
 * Managed workspaces: shows accounts that expire within 7 days (amber) or have expired/paused (red).
 * Display only — renewals are handled manually by the account manager (no online payment).
 */
export function ExpiredSeatsBanner({ workspaceId }: { workspaceId: string }) {
  const { data: seats = [] } = useAccountSubscriptions(workspaceId);
  const { locked, expiring } = useMemo(() => {
    const used = seats.filter((s) => s.client_id && s.status !== "removed");
    return {
      locked: used.filter((s) => seatState(s).tone === "destructive"),
      expiring: used.filter((s) => seatState(s).tone === "warning"),
    };
  }, [seats]);
  if (locked.length === 0 && expiring.length === 0) return null;

  const name = (s: any) => s.clients?.linkedin_name || s.clients?.email || "LinkedIn account";
  const days = (s: any) =>
    s.current_period_end ? Math.max(0, Math.ceil((new Date(s.current_period_end).getTime() - Date.now()) / 86400000)) : null;

  return (
    <div className="space-y-2">
      {locked.length > 0 && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-foreground">
          <div className="flex items-center gap-2 font-medium">
            <Lock className="h-4 w-4 shrink-0 text-destructive" />
            {locked.length === 1 ? "One LinkedIn account needs renewing" : `${locked.length} LinkedIn accounts need renewing`}
          </div>
          <p className="mt-1 text-muted-foreground">
            {locked.map(name).join(", ")}. Their campaigns are paused. Please contact your account manager to renew.
          </p>
        </div>
      )}
      {expiring.length > 0 && (
        <div className="rounded-lg border border-warning/30 bg-warning/5 px-4 py-3 text-sm text-foreground">
          <div className="flex items-center gap-2 font-medium">
            <AlertTriangle className="h-4 w-4 shrink-0 text-warning" />
            {expiring.length === 1 ? "One LinkedIn account renews soon" : `${expiring.length} LinkedIn accounts renew soon`}
          </div>
          <ul className="mt-1 space-y-0.5 text-muted-foreground">
            {expiring.map((s: any) => (
              <li key={s.id}>
                {name(s)} renews on {format(new Date(s.current_period_end), "d MMM yyyy")} ({days(s)} day{days(s) === 1 ? "" : "s"} left)
              </li>
            ))}
          </ul>
          <p className="mt-1 text-muted-foreground">Please contact your account manager to renew.</p>
        </div>
      )}
    </div>
  );
}
